import { Typography } from "@mui/material";
import {
	PENSION_ANNUITY_INTEREST_RATE,
	PENSION_ANNUITY_LIMITS,
} from "shared/CalculatorLimits";
import { getPensionAnnuityPlan } from "shared/getPensionAnnuityPlan";
import { CalculatorRateAmount } from "web_myplann/Calculators/Components/CalculatorRateAmount";
import { CalculatorResultSection } from "web_myplann/Calculators/Components/CalculatorResultSection";
import { CalculatorShell } from "web_myplann/Calculators/Components/CalculatorShell";
import { CalculatorWaitingNote } from "web_myplann/Calculators/Components/CalculatorWaitingNote";
import {
	PENSION_ANNUITY_DEFAULT_DURATION,
	PENSION_ANNUITY_DEFAULT_YEARS_UNTIL,
} from "web_myplann/Calculators/constants";
import { getPensionAnnuityFormSchema } from "web_myplann/Calculators/Forms/pensionAnnuityFormSchema";
import { useCalculatorForm } from "web_myplann/Calculators/Hooks/useCalculatorForm";
import { useCalculatorLead } from "web_myplann/Calculators/Hooks/useCalculatorLead";
import { useYearsLabel } from "web_myplann/Calculators/Hooks/useYearsLabel";
import { CalculatorType } from "web_myplann/Calculators/Types/CalculatorType";
import { hasPensionAnnuityInputs } from "web_myplann/Calculators/Utils/hasPensionAnnuityInputs";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	formatCurrency,
	getCurrencySymbol,
} from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { SliderFormRow } from "web_myplann/Shared/Components/SliderFormRow";
import { CurrencyCode } from "web_myplann/Shared/constants";

export const PensionAnnuityCalculatorPage = () => {
	const { t } = useResource();
	const yearsLabel = useYearsLabel();
	const { send, ...lead } = useCalculatorLead(CalculatorType.PensionAnnuity);
	const { control, watch, handleSubmit } = useCalculatorForm(
		getPensionAnnuityFormSchema,
		{
			duration: PENSION_ANNUITY_DEFAULT_DURATION,
			yearsUntil: PENSION_ANNUITY_DEFAULT_YEARS_UNTIL,
		},
	);

	const { annuity, deposit, duration, yearsUntil } = watch();
	const { monthlyPayment, requiredAmount, monthlyPaymentLater, waitingYears } =
		getPensionAnnuityPlan({
			monthlyAnnuity: annuity,
			annuityYears: duration,
			yearsUntilAnnuity: yearsUntil,
			initialDeposit: deposit,
			interestRate: PENSION_ANNUITY_INTEREST_RATE,
		});
	const showResult = hasPensionAnnuityInputs({ annuity, deposit });

	const onSubmit = handleSubmit((values) =>
		send({
			annuity: values.annuity,
			duration: values.duration,
			expectedWithdraw: values.yearsUntil,
			initialDeposit: values.deposit,
			installment: monthlyPayment,
			interest: PENSION_ANNUITY_INTEREST_RATE,
		}),
	);

	return (
		<CalculatorShell
			type={CalculatorType.PensionAnnuity}
			onSubmit={onSubmit}
			fields={
				<>
					<FlatRowFormInput
						control={control}
						name="annuity"
						label={t(Resources.Calculators.PensionAnnuity.MonthlyAnnuity)}
						placeholder={t(Resources.Calculators.FillPlaceholder)}
						type="number"
						max={PENSION_ANNUITY_LIMITS.annuityMax}
						suffix={getCurrencySymbol(CurrencyCode.CZK)}
					/>
					<FlatRowFormInput
						control={control}
						name="deposit"
						label={t(Resources.Calculators.PensionAnnuity.InitialDeposit)}
						placeholder={t(Resources.Calculators.FillPlaceholder)}
						type="number"
						max={PENSION_ANNUITY_LIMITS.depositMax}
						suffix={getCurrencySymbol(CurrencyCode.CZK)}
					/>
					<SliderFormRow
						control={control}
						name="duration"
						label={t(Resources.Calculators.PensionAnnuity.Duration)}
						formatValue={yearsLabel}
						min={PENSION_ANNUITY_LIMITS.durationMin}
						max={PENSION_ANNUITY_LIMITS.durationMax}
					/>
					<SliderFormRow
						control={control}
						name="yearsUntil"
						label={t(Resources.Calculators.PensionAnnuity.YearsUntil)}
						formatValue={yearsLabel}
						min={PENSION_ANNUITY_LIMITS.yearsUntilMin}
						max={PENSION_ANNUITY_LIMITS.yearsUntilMax}
						isLast
					/>
				</>
			}
			result={
				showResult && (
					<CalculatorResultSection
						badge={t(Resources.Calculators.Result.Badges.todaySave)}
						hero={
							monthlyPayment > 0 ? (
								<CalculatorRateAmount
									amount={monthlyPayment}
									rate={PENSION_ANNUITY_INTEREST_RATE}
									rateFractionDigits={0}
								/>
							) : (
								<Typography variant="bodyStrong">
									{t(
										Resources.Calculators.PensionAnnuity.ResultCoveredByDeposit,
									)}
								</Typography>
							)
						}
						rows={[
							{
								label: t(
									Resources.Calculators.PensionAnnuity.ResultRequiredAmount,
								),
								value: formatCurrency(requiredAmount, CurrencyCode.CZK, 0),
							},
						]}
						waitingNote={
							monthlyPaymentLater > monthlyPayment ? (
								<CalculatorWaitingNote
									years={waitingYears}
									later={monthlyPaymentLater}
									extra={monthlyPaymentLater - monthlyPayment}
								/>
							) : undefined
						}
						lead={lead}
					/>
				)
			}
		/>
	);
};
